import type { PlayCircleRelationshipDto } from '@/lib/play/playCircleReadService'
import type { CirclePlaydateDto } from '@/lib/play/circlePlaydateDto'
import { formatCirclePlaydateWhenLabel } from '@/lib/play/formatCirclePlaydateWhen'

export type PlayRelationshipSheetContent = {
  pairTitle: string
  contextLine: string
  firstMet?: string
  lastPlayed?: string
  timesPlayedTogether?: string
  relationshipAge?: string
  upcomingPlaydate?: string
  /** Non-interactive status (never a filled CTA). */
  statusLine?: string
  primaryCta: string
  secondaryCta: string
  hasPlannedPlaydate: boolean
  /** open playdate for this relationship when playdates flag is on */
  openPlaydate?: CirclePlaydateDto | null
  /**
   * Primary filled button work.
   * `close` — dismisses sheet (waiting status uses this; never a disabled fake CTA).
   */
  primaryAction?: 'plan' | 'review' | 'close' | 'complete'
  /** Agreed upcoming playdate can be cancelled without leaving the friendship. */
  canCancelPlaydate?: boolean
  /** When false, Plan is unavailable — sheet only offers Close. */
  playdatesAvailable?: boolean
}

function formatFirstConnected(iso: string): string | undefined {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatLastPlayed(iso: string): string | undefined {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatRelationshipAgeLabel(days: number | null | undefined): string | undefined {
  if (days == null || days < 0) return undefined
  if (days < 1) return 'Just connected'
  if (days === 1) return '1 day'
  if (days < 30) return `${days} days`
  const months = Math.floor(days / 30)
  if (months < 12) return months === 1 ? '1 month' : `${months} months`
  const years = Math.floor(days / 365)
  return years === 1 ? '1 year' : `${years} years`
}

function formatTimesPlayedTogether(playCount: number): string | undefined {
  if (playCount <= 0) return undefined
  return playCount === 1 ? '1 time' : `${playCount} times`
}

function formatUpcomingLine(playdate: CirclePlaydateDto, friendName: string): string {
  const when = formatCirclePlaydateWhenLabel({
    scheduledStartAt: playdate.scheduledStartAt,
    timezone: playdate.timezone,
    presentationMode: playdate.presentationMode ?? 'daypart',
  })
  const where = playdate.parkName || playdate.customLocationLabel || 'Public place'
  if (playdate.status === 'proposed') {
    return playdate.viewerRole === 'invitee'
      ? `Invitation · ${when} · ${where}`
      : `Invitation sent · waiting for ${friendName} · ${when} · ${where}`
  }
  if (playdate.status === 'agreed') {
    return `Confirmed · ${when} · ${where}`
  }
  return `${when} · ${where}`
}

/**
 * Production relationship sheet copy — sourced from Play Circle read APIs only.
 */
export function buildPlayRelationshipContentFromDto(input: {
  activeDogName: string
  relationship: PlayCircleRelationshipDto
  openPlaydate?: CirclePlaydateDto | null
  playdatesAvailable?: boolean
  /**
   * When true, hide We met (complete in flight or just succeeded) so the CTA
   * cannot flash back from a stale agreed open playdate.
   */
  suppressCompleteAction?: boolean
  /**
   * Explicit focused playdate id is set but not in open items — disable
   * We Met / Cancel (never fall back to preferred upcoming).
   */
  explicitPlaydateMissing?: boolean
  /** Optional clock for cancel eligibility (defaults to Date.now()). */
  now?: Date
}): PlayRelationshipSheetContent {
  const active = input.activeDogName.trim() || 'Your dog'
  const friend = input.relationship.friendDog.name.trim() || 'Friend'
  const pairTitle = `${active} + ${friend}`
  const metAt = input.relationship.context.metAtParkName?.trim()
  const firstConnected = formatFirstConnected(input.relationship.firstConnectedAt)
  const playdatesAvailable = input.playdatesAvailable !== false
  const suppressCompleteAction = input.suppressCompleteAction === true
  const explicitPlaydateMissing = input.explicitPlaydateMissing === true
  const nowMs = (input.now ?? new Date()).getTime()

  const contextLine = metAt
    ? `${active} and ${friend} connected after a real meeting at ${metAt}.`
    : `${active} and ${friend} are trusted play friends.`

  const firstMet = metAt
    ? `Met at ${metAt}${firstConnected ? ` · connected ${firstConnected}` : ''}`
    : firstConnected
      ? `Connected ${firstConnected}`
      : undefined

  const playStats = input.relationship.playStats
  const lastPlayed = playStats?.lastPlayedAt
    ? formatLastPlayed(playStats.lastPlayedAt)
    : undefined
  const timesPlayedTogether = formatTimesPlayedTogether(playStats?.playCount ?? 0)
  const relationshipAge = formatRelationshipAgeLabel(playStats?.relationshipAgeDays)

  const openPlaydate = input.openPlaydate ?? null
  let primaryCta = 'Plan a playdate'
  let primaryAction: PlayRelationshipSheetContent['primaryAction'] = 'plan'
  let upcomingPlaydate: string | undefined
  let statusLine: string | undefined
  let hasPlannedPlaydate = false

  if (!playdatesAvailable) {
    primaryCta = 'Close'
    primaryAction = 'close'
  } else if (explicitPlaydateMissing) {
    primaryCta = 'Close'
    primaryAction = 'close'
    statusLine = 'That playdate is no longer open.'
  } else if (openPlaydate) {
    upcomingPlaydate = formatUpcomingLine(openPlaydate, friend)
    hasPlannedPlaydate = true
    if (openPlaydate.status === 'proposed' && openPlaydate.viewerRole === 'invitee') {
      primaryCta = 'Review invitation'
      primaryAction = 'review'
    } else if (openPlaydate.status === 'proposed') {
      statusLine = `Waiting for ${friend}`
      primaryCta = 'Close'
      primaryAction = 'close'
    } else if (openPlaydate.status === 'agreed') {
      // Persisted status is agreed (upcoming/confirmed). Next meetup line already
      // carries "Confirmed · when · where" — omit duplicate statusLine.
      if (suppressCompleteAction) {
        primaryCta = 'Close'
        primaryAction = 'close'
      } else {
        primaryCta = 'We met'
        primaryAction = 'complete'
      }
    } else {
      // Terminal/unexpected open statuses — no dead View CTA
      primaryCta = 'Close'
      primaryAction = 'close'
    }
  }

  const canCancelPlaydate =
    primaryAction === 'complete' &&
    openPlaydate != null &&
    new Date(openPlaydate.scheduledStartAt).getTime() > nowMs

  return {
    pairTitle,
    contextLine,
    firstMet,
    lastPlayed,
    timesPlayedTogether,
    relationshipAge,
    upcomingPlaydate,
    statusLine,
    primaryCta,
    secondaryCta: 'Close',
    hasPlannedPlaydate,
    openPlaydate,
    primaryAction,
    canCancelPlaydate,
    playdatesAvailable,
  }
}
